import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Icon,
  Message,
  Loader,
  Select,
  Segment,
  Header,
} from "semantic-ui-react";
import { map, size } from "lodash";
import { PeriodGrade, AcademicPeriod } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { LiteralGradeSelect } from "../../../Shared/LiteralGradeSelect";
import "./GradeRegistrationTable.scss";

const periodGradeController = new PeriodGrade();
const academicPeriodController = new AcademicPeriod();

export function GradeRegistrationTable(props) {
  const { academicCycleId, subjectId, gradeId } = props;
  const { accessToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  // Data from the unified endpoint
  const [students, setStudents] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [grades, setGrades] = useState({});
  const [existingGrades, setExistingGrades] = useState({});

  // Load periods
  useEffect(() => {
    if (!academicCycleId) return;

    (async () => {
      try {
        const response = await academicPeriodController.getPeriodsByCycle(
          accessToken,
          academicCycleId,
        );
        setPeriods(response);
        // Select first active period by default
        const activePeriod = response.find((p) => p.status === 1);
        if (activePeriod) {
          setSelectedPeriod(activePeriod.id);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [academicCycleId, accessToken]);

  // Load all data when filters change (unified endpoint)
  useEffect(() => {
    if (!academicCycleId || !subjectId || !gradeId || !selectedPeriod) {
      setStudents([]);
      setCompetences([]);
      setGrades({});
      setExistingGrades({});
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const response = await periodGradeController.getGradesByFilters(
          accessToken,
          academicCycleId,
          gradeId,
          subjectId,
          selectedPeriod,
        );

        // Set students
        setStudents(response.students || []);

        // Set competences
        setCompetences(response.competences || []);

        // Process grades into a map: courseId-competenceId -> literalGrade
        const gradesMap = {};
        if (response.grades) {
          response.grades.forEach((studentGrade) => {
            const courseId = studentGrade.courseId;
            if (studentGrade.grades) {
              Object.entries(studentGrade.grades).forEach(
                ([competenceId, gradeData]) => {
                  const key = `${courseId}-${competenceId}`;
                  gradesMap[key] = gradeData.literalGrade;
                },
              );
            }
          });
        }
        setExistingGrades({ ...gradesMap });
        setGrades({ ...gradesMap });

        setLoading(false);
      } catch (error) {
        console.error(error);
        setStudents([]);
        setCompetences([]);
        setLoading(false);
      }
    })();
  }, [academicCycleId, subjectId, gradeId, selectedPeriod, accessToken]);

  const handleGradeChange = useCallback((courseId, competenceId, value) => {
    const key = `${courseId}-${competenceId}`;
    setGrades((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSaveGrades = async () => {
    try {
      setSaving(true);
      const gradesToSave = [];

      Object.entries(grades).forEach(([key, literalGrade]) => {
        if (literalGrade && literalGrade !== existingGrades[key]) {
          const [courseId, competenceId] = key.split("-");
          gradesToSave.push({
            courseId: parseInt(courseId),
            academicPeriodId: selectedPeriod,
            competenceId: parseInt(competenceId),
            literalGrade,
            observations: "",
          });
        }
      });

      if (gradesToSave.length > 0) {
        await periodGradeController.registerBulkGrades(
          accessToken,
          gradesToSave,
        );
        setExistingGrades({ ...grades });
      }
      setSaving(false);
    } catch (error) {
      console.error(error);
      setSaving(false);
    }
  };

  const periodOptions = [
    ...periods.map((p) => ({
      key: p.id,
      value: p.id,
      text: p.name,
      disabled: p.status === 2,
    })),
  ];

  const selectedPeriodData = periods.find((p) => p.id === selectedPeriod);
  const isPeriodClosed = selectedPeriodData?.status === 2;

  if (!subjectId || !gradeId) {
    return (
      <Segment className="grade-registration-table__empty">
        <Icon name="clipboard list" size="huge" />
        <p>
          Para registrar notas, primero selecciona una asignatura y un grado.
        </p>
      </Segment>
    );
  }

  if (!selectedPeriod) {
    return (
      <Segment info>
        <Header>Selecciona un período</Header>
        <p>No hay períodos académicos activos para este ciclo.</p>
      </Segment>
    );
  }

  if (loading) {
    return <Loader active inline="centered" content="Cargando datos..." />;
  }

  if (size(students) === 0) {
    return (
      <Message warning>
        <Message.Header>No hay estudiantes</Message.Header>
        <p>
          No se encontraron estudiantes matriculados para esta asignatura y
          grado.
        </p>
      </Message>
    );
  }

  if (size(competences) === 0) {
    return (
      <Message warning>
        <Message.Header>No hay competencias</Message.Header>
        <p>No se han configurado competencias para esta asignatura y grado.</p>
      </Message>
    );
  }

  return (
    <div className="grade-registration-table">
      <Segment className="grade-registration-table__header">
        <div className="grade-registration-table__controls">
          <div className="grade-registration-table__period-select">
            <label>Período:</label>
            <Select
              className="edunova-select"
              options={periodOptions}
              value={selectedPeriod}
              onChange={(_, data) => setSelectedPeriod(data.value)}
              placeholder="Selecciona período"
            />
          </div>
          <Button
            primary
            onClick={handleSaveGrades}
            loading={saving}
            disabled={isPeriodClosed}
          >
            <Icon name="save" />
            Guardar Notas
          </Button>
        </div>
        {isPeriodClosed && (
          <Message warning size="small">
            <Icon name="lock" />
            El período seleccionado está cerrado. No se pueden registrar notas.
          </Message>
        )}
      </Segment>

      <div className="grade-registration-table__scroll">
        <Table celled structured unstackable className="grade-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell rowSpan="2">#</Table.HeaderCell>
              <Table.HeaderCell rowSpan="2">Estudiante</Table.HeaderCell>
              <Table.HeaderCell colSpan={size(competences)} textAlign="center">
                Competencias
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              {map(competences, (comp) => (
                <Table.HeaderCell
                  key={comp.id}
                  textAlign="center"
                  className="competence-header"
                >
                  <div className="competence-header__code">{comp.code}</div>
                  <div className="competence-header__name">{comp.name}</div>
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map(students, (student, index) => (
              <Table.Row key={student.courseId}>
                <Table.Cell textAlign="center">{index + 1}</Table.Cell>
                <Table.Cell className="student-name">
                  {student.lastName}, {student.firstName}
                </Table.Cell>
                {map(competences, (comp) => {
                  const key = `${student.courseId}-${comp.id}`;
                  return (
                    <Table.Cell
                      key={comp.id}
                      textAlign="center"
                      className="grade-cell"
                    >
                      <LiteralGradeSelect
                        value={grades[key] || ""}
                        onChange={(value) =>
                          handleGradeChange(student.courseId, comp.id, value)
                        }
                        disabled={isPeriodClosed}
                        compact
                      />
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
}
